import { Card, CardBody, CardFooter, HStack, Avatar, Box, Heading, Text, Badge, Button, Icon, SimpleGrid } from '@chakra-ui/react';
import { FiCheck, FiBookOpen, FiAlertCircle } from 'react-icons/fi';
import { IoCarSportOutline } from 'react-icons/io5';
import { keyframes } from '@emotion/react';
import { getPrimaryClassName } from '../utils/classUtils';

export const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(229, 62, 62, 0.7); }
  70% { transform: scale(1.02); box-shadow: 0 0 0 10px rgba(229, 62, 62, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(229, 62, 62, 0); }
`;

export const STATUS_CONFIG = {
    'NOT_ARRIVED': { label: '未到班', color: 'gray', priority: 6, isUrgent: false },
    'ARRIVED': { label: '已進班', color: 'blue', priority: 5, isUrgent: false },
    'HOMEWORK_PENDING': { label: '留班加強', color: 'orange', priority: 4, isUrgent: false },
    'READY_FOR_PICKUP': { label: '可接送', color: 'green', priority: 2, isUrgent: false },
    'PARENT_EN_ROUTE': { label: '家長路途中', color: 'red', priority: 1, isUrgent: true },
    'PICKUP_COMPLETED': { label: '已離校', color: 'gray', priority: 7, isUrgent: false },
    'LEAVE': { label: '請假', color: 'purple', priority: 8, isUrgent: false }, // Added LEAVE status
};

const StudentCard = ({ student, onUpdate }) => {
    const config = STATUS_CONFIG[student.status] || STATUS_CONFIG['NOT_ARRIVED'];
    const isUrgent = config.isUrgent;

    return (
        <Card
            borderLeftWidth="5px"
            borderColor={`${config.color}.500`}
            shadow={isUrgent ? "xl" : "md"}
            transform={isUrgent ? "scale(1.02)" : "scale(1)"}
            transition="all 0.3s"
            animation={isUrgent ? `${pulse} 2s infinite` : undefined}
            bg={student.status === 'PICKUP_COMPLETED' ? 'gray.50' : 'white'}
            opacity={student.status === 'PICKUP_COMPLETED' ? 0.8 : 1}
            height="100%"
            minH={{ base: "92px", md: "240px" }} // 手機：緊湊單行卡；桌面：原尺寸不變
            display="flex"
            flexDirection="column"
        >
            <CardBody p={{ base: 2, md: 6 }} pb={{ base: 1, md: 2 }} flex="1">
                <HStack spacing={{ base: 2, md: 4 }} mb={{ base: 1, md: 3 }}>
                    <Avatar size={{ base: "sm", md: "md" }} name={student.full_name} bg={`${config.color}.500`} color="white" />

                    <Box flex="1" overflow="hidden">
                        <Heading size={{ base: "sm", md: "md" }} color="gray.700" isTruncated>{student.full_name}</Heading>
                        <Text fontSize={{ base: "10px", md: "sm" }} color="gray.500" isTruncated>{getPrimaryClassName(student)}</Text>
                    </Box>
                    <Box ml="auto">
                        <Badge fontSize={{ base: "9px", md: "0.8em" }} colorScheme={config.color} variant="solid" px={{ base: 1.5, md: 2 }} py={{ base: 0.5, md: 1.5 }} borderRadius="md">
                            {config.label}
                        </Badge>
                    </Box>
                </HStack>

                {isUrgent && (
                    <HStack bg="red.50" p={{ base: 1, md: 2 }} borderRadius="md" color="red.600">
                        <Icon as={FiAlertCircle} boxSize={{ base: "12px", md: "16px" }} />
                        <Text fontSize={{ base: "10px", md: "sm" }} fontWeight="bold">家長即將抵達！</Text>
                    </HStack>
                )}
            </CardBody>
            <CardFooter p={{ base: 2, md: 6 }} pt={0} display="flex" flexDirection="column" gap={{ base: 1, md: 2 }} mt="auto">

                {/* Actions for ARRIVED (已進班) */}
                {student.status === 'ARRIVED' && (
                    <SimpleGrid columns={2} spacing={{ base: 1, md: 2 }} w="100%">
                        <Button
                            leftIcon={<FiBookOpen />}
                            colorScheme="orange"
                            variant="outline"
                            size={{ base: "xs", md: "sm" }}
                            fontSize={{ base: "10px", md: "xs" }}
                            onClick={() => onUpdate(student.id, 'HOMEWORK_PENDING')}
                        >
                            留班加強
                        </Button>
                        <Button
                            leftIcon={<FiCheck />}
                            colorScheme="green"
                            size={{ base: "xs", md: "sm" }}
                            fontSize={{ base: "10px", md: "xs" }}
                            onClick={() => onUpdate(student.id, 'READY_FOR_PICKUP')}
                        >
                            作業完成
                        </Button>
                    </SimpleGrid>
                )}

                {/* Actions for HOMEWORK_PENDING (留班加強) */}
                {student.status === 'HOMEWORK_PENDING' && (
                    <Button
                        leftIcon={<FiCheck />}
                        colorScheme="green"
                        size={{ base: "xs", md: "sm" }}
                        fontSize={{ base: "10px", md: "sm" }}
                        w="100%"
                        onClick={() => onUpdate(student.id, 'READY_FOR_PICKUP')}
                    >
                        作業完成 (可接送)
                    </Button>
                )}

                {/* Actions for NOT_ARRIVED (補點名) */}
                {student.status === 'NOT_ARRIVED' && (
                    <Button
                        size={{ base: "xs", md: "sm" }}
                        fontSize={{ base: "10px", md: "sm" }}
                        colorScheme="blue"
                        variant="ghost"
                        w="100%"
                        onClick={() => onUpdate(student.id, 'ARRIVED')}
                    >
                        補按進班
                    </Button>
                )}

                {/* Actions for PARENT_EN_ROUTE */}
                {student.status === 'PARENT_EN_ROUTE' && (
                    <Box bg="orange.50" p={{ base: 1.5, md: 3 }} borderRadius="md" w="100%" textAlign="center">
                        <Text fontSize={{ base: "10px", md: "sm" }} fontWeight="bold" color="orange.700">家長即將抵達</Text>
                        <Text fontSize={{ base: "xs", md: "md" }} fontWeight="bold" color="red.600">
                            預計 {student.eta_minutes || 5} 分鐘後到達
                        </Text>
                        <Text fontSize="xs" color="gray.500" display={{ base: 'none', md: 'block' }}>
                            (及時 GPS 資訊)
                        </Text>
                        {/* [A3] 本次接送車號：有值才顯示（天籟家長沒有登錄車號 → 恆不顯示，零影響） */}
                        {student.active_pickup_car_plate && (
                            <HStack justify="center" mt={1} spacing={1}>
                                <Icon as={IoCarSportOutline} boxSize={3.5} color="orange.600" />
                                <Text fontSize="sm" fontWeight="bold" color="orange.700">
                                    {student.active_pickup_car_plate}
                                </Text>
                            </HStack>
                        )}
                    </Box>
                )}

                {/* READY_FOR_PICKUP intentionally has no completion action on My Class page */}
                {false && student.status === 'READY_FOR_PICKUP' && (
                    <Button
                        size="sm"
                        colorScheme="gray"
                        variant="outline"
                        w="100%"
                        onClick={() => onUpdate(student.id, 'PICKUP_COMPLETED')}
                    >
                        家長已接走
                    </Button>
                )}
            </CardFooter>
        </Card>
    );
};

export default StudentCard;
